import { t } from "@lingui/core/macro";
import {
  addMonths,
  endOfMonth,
  format,
  parse,
  startOfMonth,
  subMonths,
} from "date-fns";
import { Provider as AtomProvider, useSetAtom } from "jotai";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { Pill } from "../../display/Pill";
import { CloseIconSmall } from "../../icons_v2/general";
import { Menu } from "../../overlay/Menu";
import { OpenPosition } from "../../overlay/Menu/types";
import { Box, HStack, Pressable, VStack } from "../../primitive";
import { calendarSettingsAtom } from "../Datepickers/components/Calendar/atoms";
import { generateDateInfo } from "../Datepickers/components/Calendar/utils/calendarDateUtils";
import { DatePickerPopup } from "../Datepickers/components/DatepickerInput/components/DatePickerPopup";
import { InputSize } from "../Input/types";
import { Trigger } from "../Trigger/Trigger";

export type MultiDatePillSelectProps = {
  /** Selected dates as `yyyy-MM-dd` strings. */
  values: string[];
  onChange: (values: string[]) => void;
  clubWeekStartsOn: NumericalDayOfWeek;
  firstAvailableDate?: string;
  lastAvailableDate?: string;
  unavailableDates?: string[];
  fillEmptyDays?: boolean;
  placeholder?: string;
  size?: InputSize;
  isDisabled?: boolean;
  testID?: string;
  openPosition?: OpenPosition;
};

const pillDateDisplayFormat = "MMM do, yyyy";

const MultiDatePillSelectInner = ({
  values,
  onChange,
  clubWeekStartsOn,
  firstAvailableDate,
  lastAvailableDate,
  unavailableDates,
  fillEmptyDays = true,
  placeholder = t`Add dates`,
  size = "regular",
  isDisabled = false,
  testID,
  openPosition = "bottom-start",
}: MultiDatePillSelectProps) => {
  const setCalendarSettings = useSetAtom(calendarSettingsAtom);

  const todayIso = format(new Date(), "yyyy-MM-dd", {
    weekStartsOn: clubWeekStartsOn,
  });

  const anchorIso = values.length ? values[values.length - 1] : todayIso;

  const [monthDateObj, setMonthDateObj] = useState(() =>
    parse(anchorIso, "yyyy-MM-dd", new Date())
  );

  const calendarStartDateObject = useMemo(
    () =>
      firstAvailableDate
        ? parse(firstAvailableDate, "yyyy-MM-dd", new Date())
        : startOfMonth(subMonths(monthDateObj, 1)),
    [firstAvailableDate, monthDateObj]
  );

  const calendarEndDateObject = useMemo(
    () =>
      lastAvailableDate
        ? parse(lastAvailableDate, "yyyy-MM-dd", new Date())
        : endOfMonth(addMonths(monthDateObj, 1)),
    [lastAvailableDate, monthDateObj]
  );

  const calendarStartDate = firstAvailableDate
    ? firstAvailableDate
    : format(calendarStartDateObject, "yyyy-MM-dd", {
        weekStartsOn: clubWeekStartsOn,
      });

  const calendarEndDate = lastAvailableDate
    ? lastAvailableDate
    : format(calendarEndDateObject, "yyyy-MM-dd", {
        weekStartsOn: clubWeekStartsOn,
      });

  const dateInfo = useMemo(() => {
    const map = generateDateInfo(
      calendarStartDateObject,
      calendarEndDateObject,
      "Default"
    );
    values.forEach((iso) => {
      const prev = map.get(iso);
      map.set(iso, {
        ...prev,
        status: prev?.status ?? "Default",
        selected: true,
      });
    });
    unavailableDates?.forEach((date) => {
      map.set(date, { status: "Unavailable" });
    });
    return map;
  }, [
    calendarStartDateObject,
    calendarEndDateObject,
    values,
    unavailableDates,
  ]);

  const handleDatePress = useCallback(
    (isoDate: string) => {
      if (values.includes(isoDate)) {
        return;
      }
      if (unavailableDates?.includes(isoDate)) {
        return;
      }
      onChange([...values, isoDate].sort());
    },
    [values, unavailableDates, onChange]
  );

  useEffect(() => {
    setCalendarSettings({
      startDate: calendarStartDate,
      endDate: calendarEndDate,
      onDatePress: handleDatePress,
      fillEmptyDays,
      dateInfo,
      singleDatePicker: true,
      discreteMultiSelect: true,
      clubWeekStartsOn,
    });
  }, [
    calendarStartDate,
    calendarEndDate,
    handleDatePress,
    fillEmptyDays,
    dateInfo,
    clubWeekStartsOn,
    setCalendarSettings,
  ]);

  const valuesList = useMemo(() => {
    return values.map((iso) => {
      const dateObj = parse(iso, "yyyy-MM-dd", new Date());
      const label = format(dateObj, pillDateDisplayFormat);
      return (
        <Pressable
          key={iso}
          testID={`multidate-pill-${iso}`}
          onPress={() => {
            onChange(values.filter((v) => v !== iso));
          }}
        >
          <Pill
            size="sm"
            label={label}
            trailingElement={<CloseIconSmall />}
            testID={`multidate-pill-text-${iso}`}
          />
        </Pressable>
      );
    });
  }, [values, onChange]);

  return (
    <VStack gap={2} position="relative">
      {valuesList.length > 0 ? (
        <HStack gap={1} flexWrap="wrap">
          {valuesList}
        </HStack>
      ) : null}

      <Menu
        openPosition={openPosition}
        trigger={({ onPress }) => (
          <Box>
            <Trigger
              size={size}
              onPress={() => {
                if (!isDisabled) {
                  onPress();
                }
              }}
              testID={testID}
              placeholder={placeholder}
              selectedItem={null}
              isDisabled={isDisabled}
              hasError={false}
              includeLeftElementInSelectedValue={false}
              hideChevron
              hideLabelInDisplay={false}
              hideSubLabelInDisplay={false}
            />
          </Box>
        )}
        content={() => (
          <Menu.Content size="large" maxHeight="100%">
            <DatePickerPopup
              monthDateObj={monthDateObj}
              startDate={startOfMonth(monthDateObj)}
              endDate={endOfMonth(monthDateObj)}
              setMonthDateObj={setMonthDateObj}
              clubWeekStartsOn={clubWeekStartsOn}
            />
          </Menu.Content>
        )}
      />
    </VStack>
  );
};

export const MultiDatePillSelect = (props: MultiDatePillSelectProps) => {
  return (
    <AtomProvider>
      <MultiDatePillSelectInner {...props} />
    </AtomProvider>
  );
};
